import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, LogOut, Search, Download, RefreshCw, Mail, Send, Globe, ShieldCheck, Inbox, MessageSquare, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from '@/components/ui/drawer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import SEOHead from '@/components/marketing/SEOHead';
import BrandLogo from '@/components/branding/BrandLogo';
import { SkeletonBlock, SkeletonTableRows } from '@/components/transitions/Skeleton';
import CenterRouteLoader from '@/components/transitions/CenterRouteLoader';
import { api, adminHeaders, getAdminToken, setAdminToken, clearAdminToken, API } from '@/lib/api';

const STATUSES = ['new', 'contacted', 'qualified', 'approved', 'rejected'];
const STATUS_VARIANT = {
  new: 'bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/30',
  contacted: 'bg-[#7C3AED]/15 text-[#A78BFA] border-[#7C3AED]/30',
  qualified: 'bg-[#2563EB]/15 text-[#60A5FA] border-[#2563EB]/30',
  approved: 'bg-[#10B981]/15 text-[#34D399] border-[#10B981]/30',
  rejected: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
};

export default function AdminLeads() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  return (
    <div className="min-h-screen bg-[#07111F] text-white">
      <SEOHead title="Admin · Leads" description="Ads Dashbound admin leads viewer." path="/admin/leads" noindex />
      <Toaster richColors position="top-right" />
      <header className="sticky top-0 z-40 bg-[#07111F]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center group gap-2" data-testid="admin-home-link">
            <BrandLogo variant="full" size={32} />
            <span className="text-slate-400 text-xs font-medium pl-1 border-l border-white/10 ml-1">Admin</span>
          </Link>
          {authed && (
            <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10" onClick={() => { clearAdminToken(); setAuthed(false); toast.success('Signed out'); }} data-testid="admin-logout-button">
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          )}
        </div>
      </header>
      {authed ? <Dashboard onUnauth={() => { clearAdminToken(); setAuthed(false); }} /> : <LoginGate onAuth={() => setAuthed(true)} />}
    </div>
  );
}

function LoginGate({ onAuth }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/admin/login', { password });
      setAdminToken(r.data.token);
      toast.success('Signed in');
      onAuth();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Login failed';
      toast.error(typeof msg === 'string' ? msg : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white/5 border border-white/10 p-8 backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.4)]" data-testid="admin-login-form">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#22D3EE] border border-[#22D3EE]/30">
          <Lock className="h-5 w-5" />
        </span>
        <h1 className="font-display mt-4 text-2xl font-bold">Admin sign-in</h1>
        <p className="mt-1 text-sm text-slate-400">Enter the admin password to view advertiser leads and contact messages.</p>
        <div className="mt-5 space-y-1.5">
          <Label className="text-sm text-slate-300">Password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="admin-password-input"
            className="bg-[#0B172A] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#22D3EE]/50"
            autoFocus
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-submit"
          className="mt-5 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl py-6 font-semibold transition-colors"
        >
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : 'Sign in'}
        </Button>
        <p className="mt-4 text-xs text-slate-500">Configure via <code className="text-slate-300">ADMIN_PASSWORD</code> in backend env.</p>
      </form>
    </main>
  );
}

function Dashboard({ onUnauth }) {
  const [tab, setTab] = useState('leads');
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl">
          <TabsTrigger value="leads" data-testid="admin-tab-leads" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white text-slate-300 rounded-lg px-4 py-2 transition-colors">
            <Inbox className="h-4 w-4 mr-1.5" /> Advertiser Leads
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white text-slate-300 rounded-lg px-4 py-2 transition-colors">
            <MessageSquare className="h-4 w-4 mr-1.5" /> Contact Messages
          </TabsTrigger>
        </TabsList>
        <TabsContent value="leads" className="mt-6">
          <LeadsView onUnauth={onUnauth} />
        </TabsContent>
        <TabsContent value="messages" className="mt-6">
          <MessagesView onUnauth={onUnauth} />
        </TabsContent>
      </Tabs>
    </main>
  );
}

function LeadsView({ onUnauth }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/leads', {
        params: { q: q || undefined, status_filter: statusFilter },
        headers: adminHeaders(),
      });
      setLeads(r.data.leads || []);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error('Session expired'); onUnauth(); }
      else { toast.error('Failed to load leads'); }
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, onUnauth]);

  useEffect(() => { load(); }, [load]);

  async function changeStatus(lead, status) {
    setUpdating(true);
    try {
      const r = await api.patch(`/admin/leads/${lead.id}/status`, { status }, { headers: adminHeaders() });
      const updated = r.data.lead;
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? updated : l)));
      setSelected(updated);
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to update status';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update');
    } finally {
      setUpdating(false);
    }
  }

  async function exportCsv() {
    try {
      const token = getAdminToken();
      const res = await fetch(`${API}/admin/leads/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashbound_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    }
  }

  const counts = useMemo(() => {
    const c = { all: leads.length };
    STATUSES.forEach((s) => { c[s] = leads.filter((l) => l.status === s).length; });
    return c;
  }, [leads]);

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {loading && leads.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              data-testid="admin-stat-skeleton"
              className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-xl space-y-2"
            >
              <SkeletonBlock className="h-3 w-12" />
              <SkeletonBlock className="h-7 w-16" />
            </div>
          ))
        ) : (
          <>
            <StatCard label="Total" value={counts.all} />
            {STATUSES.map((s) => <StatCard key={s} label={s} value={counts[s] || 0} accent />)}
          </>
        )}
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-xl">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="admin-leads-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, company, email, vertical, format, country…"
              className="pl-9 bg-[#0B172A] border-white/10 text-white placeholder:text-slate-500"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-leads-status-filter" className="lg:w-48 bg-[#0B172A] border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" className="bg-white/5 text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl transition-colors" data-testid="admin-leads-refresh-button">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={exportCsv} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-xl transition-colors" data-testid="admin-leads-export-csv-button">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full text-sm" data-testid="admin-leads-table">
            <thead className="bg-[#0B172A] text-slate-300">
              <tr>
                <Th>Submitted</Th><Th>Name</Th><Th>Company</Th><Th>Email</Th>
                <Th>Vertical</Th><Th>Format</Th><Th>Budget</Th><Th>Status</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 bg-[#07111F]">
              {loading ? (
                <SkeletonTableRows rows={5} columns={9} testId="admin-leads-row-skeleton" />
              ) : leads.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400">No leads yet. Submissions from the public signup form will appear here.</td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} className="hover:bg-white/5 transition-colors" data-testid="admin-leads-row">
                  <Td className="text-slate-400 text-xs whitespace-nowrap">{formatDate(l.createdAt)}</Td>
                  <Td className="font-medium">{l.fullName}</Td>
                  <Td>{l.companyName}</Td>
                  <Td className="text-slate-300">{l.email}</Td>
                  <Td>{l.mainVertical}</Td>
                  <Td>{l.trafficFormat}</Td>
                  <Td className="text-xs">{l.estimatedDailyBudget}</Td>
                  <Td><StatusPill status={l.status} /></Td>
                  <Td>
                    <Button size="sm" variant="ghost" data-testid="admin-leads-open-detail-button" className="text-[#22D3EE] hover:bg-[#22D3EE]/10" onClick={() => setSelected(l)}>View</Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DrawerContent className="bg-[#0B172A] text-white border-t border-white/10 max-h-[92vh]">
          {selected && (
            <>
              <DrawerHeader className="text-left">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <DrawerTitle className="font-display text-xl">{selected.fullName} <span className="text-slate-400 font-normal text-sm">— {selected.companyName}</span></DrawerTitle>
                    <DrawerDescription className="text-slate-400">Submitted {formatDate(selected.createdAt)}</DrawerDescription>
                  </div>
                  <StatusPill status={selected.status} />
                </div>
              </DrawerHeader>
              <div className="px-4 sm:px-6 pb-4 overflow-y-auto">
                <div className="grid sm:grid-cols-2 gap-3">
                  <KV icon={Mail} k="Email" v={selected.email} />
                  <KV icon={Send} k="Contact" v={selected.contactHandle} />
                  <KV icon={Globe} k="Country" v={selected.country} />
                  <KV icon={ShieldCheck} k="Compliance" v={selected.complianceConfirmed ? 'Confirmed' : 'Not confirmed'} />
                  <KV k="Website" v={selected.website || '—'} />
                  <KV k="Vertical" v={selected.mainVertical} />
                  <KV k="Traffic format" v={selected.trafficFormat} />
                  <KV k="Daily budget" v={selected.estimatedDailyBudget} />
                  <KV k="Target GEOs" v={selected.targetGeos} />
                  <KV k="Tracking" v={selected.trackingPlatform} />
                  <KV k="Monthly spend" v={selected.monthlyAdSpend || '—'} />
                  <KV k="Launch date" v={selected.preferredLaunchDate || '—'} />
                  <KV k="Manager support" v={selected.needsManagerSupport ? 'Yes' : 'No'} />
                  <KV k="IP" v={selected.ipAddress || '—'} />
                </div>
                {Array.isArray(selected.offerUrls) && selected.offerUrls.length > 0 && (
                  <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4">
                    <h4 className="font-display text-sm font-semibold">Offer URLs</h4>
                    <ul className="mt-2 space-y-1 text-sm break-all">
                      {selected.offerUrls.map((u, i) => (
                        <li key={i}><a href={u.startsWith('http') ? u : `https://${u}`} target="_blank" rel="noreferrer noopener" className="text-[#22D3EE] hover:underline">{u}</a></li>
                      ))}
                    </ul>
                  </div>
                )}
                {selected.message && (
                  <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4">
                    <h4 className="font-display text-sm font-semibold">Message</h4>
                    <p className="mt-2 text-sm text-slate-300 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}
              </div>
              <DrawerFooter className="flex-col sm:flex-row gap-2 border-t border-white/10 pt-4">
                <div className="flex flex-wrap gap-2 mr-auto">
                  {STATUSES.map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      disabled={updating || selected.status === s}
                      onClick={() => changeStatus(selected, s)}
                      data-testid={`admin-leads-set-status-${s}`}
                      className={`rounded-lg transition-colors ${selected.status === s ? 'bg-white/15 text-white' : 'bg-white/5 text-slate-200 hover:bg-white/10 border border-white/10'}`}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
                <Button variant="ghost" onClick={() => setSelected(null)} className="text-slate-300 hover:bg-white/10">Close</Button>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}

function MessagesView({ onUnauth }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/contact-messages', { headers: adminHeaders() });
      setMsgs(r.data.messages || []);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error('Session expired'); onUnauth(); }
      else toast.error('Failed to load messages');
    } finally { setLoading(false); }
  }, [onUnauth]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-xl">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold">Contact messages</h2>
        <Button size="sm" onClick={load} variant="outline" className="bg-white/5 text-white border-white/15 hover:bg-white/10 hover:text-white rounded-lg transition-colors" data-testid="admin-messages-refresh-button">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="min-w-full text-sm">
          <thead className="bg-[#0B172A] text-slate-300">
            <tr><Th>Submitted</Th><Th>Name</Th><Th>Email</Th><Th>Company</Th><Th>Message</Th></tr>
          </thead>
          <tbody className="divide-y divide-white/5 bg-[#07111F]">
            {loading ? (
              <SkeletonTableRows rows={4} columns={5} testId="admin-messages-row-skeleton" />
            ) : msgs.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-16 text-center text-slate-400">No contact messages yet.</td></tr>
            ) : msgs.map((m) => (
              <tr key={m.id} className="align-top">
                <Td className="text-slate-400 text-xs whitespace-nowrap">{formatDate(m.createdAt)}</Td>
                <Td className="font-medium">{m.name}</Td>
                <Td className="text-slate-300">{m.email}</Td>
                <Td>{m.company || '—'}</Td>
                <Td className="max-w-md text-slate-300"><span className="block whitespace-pre-wrap">{m.message}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-xl" data-testid="admin-stat-card">
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`font-display mt-1 text-2xl font-extrabold ${accent ? 'text-[#22D3EE]' : 'text-white'}`}>{value}</div>
    </div>
  );
}
function Th({ children }) { return <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">{children}</th>; }
function Td({ children, className = '' }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
function StatusPill({ status }) {
  const cls = STATUS_VARIANT[status] || STATUS_VARIANT.new;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>{status}</span>;
}
function KV({ icon: Icon, k, v }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 inline-flex items-center gap-1">{Icon ? <Icon className="h-3 w-3" /> : null} {k}</div>
      <div className="mt-1 text-sm text-white break-words">{v || '—'}</div>
    </div>
  );
}
function formatDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
